import { AccessTime, ArrowDropDown, Inbox } from '@mui/icons-material';
import {
  Box,
  LinearProgress,
  MenuItem,
  MenuList,
  Popover,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BuildCartEntity, BuildChecklistEntity } from '@peritasai/repository';
import {
  Button as UIButton,
  Typography as UITypography,
} from '@peritasai/ui-components';
import {
  componentTokens,
  themeColors as periverseColors,
  periverseTheme,
  themeShape,
} from '@peritasai/ui-shared';
import React, { useState } from 'react';
import BuildCartIcon from '../../../../assets/svg/build-cart-icon.svg';

const CardIconWrapper = styled(Box)(({ theme }) => ({
  color: periverseTheme.palette.text.primary,
  borderRadius: '50%',
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginRight: theme.spacing(1),
}));
/**
 * Utility function to calculate time until surgery
 * Returns format like "Starts in 2 Hours" or "Starts in 30 Minutes"
 */
function calculateTimeUntilSurgery(dateTime: Date): string {
  const now = new Date();
  const diffMs = dateTime.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffMs < 0) {
    return 'Overdue';
  }

  if (diffHours > 0) {
    return `Starts in ${diffHours} ${diffHours === 1 ? 'Hour' : 'Hours'}`;
  } else if (diffMinutes > 0) {
    return `Starts in ${diffMinutes} ${
      diffMinutes === 1 ? 'Minute' : 'Minutes'
    }`;
  } else {
    return 'Starting soon';
  }
}

interface BuildCartHeaderProps {
  buildCart: BuildCartEntity;
  caseData: {
    id: string;
    caseHospitalId: string;
    caseType: string;
    formattedStartTime: string;
    surgeon: string;
    room: string;
    dateTime: Date;
    cartCompletenessStatus?: string | null;
  };
  buildChecklist?: BuildChecklistEntity;
  onSaveAndClose: () => void | Promise<void>;
  onNext: () => void | Promise<void>;
}

const StatusBadge = styled(Box)(() => ({
  backgroundColor: periverseColors.peritasgrey[700], // Dark grey background
  color: periverseTheme.palette.common.white, // White text
  borderRadius: themeShape.borderRadius.medium, // Rounded corners
  padding: '4px 12px',
  fontSize: '0.875rem',
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
}));

export const BuildCartHeader: React.FC<BuildCartHeaderProps> = ({
  buildCart,
  caseData,
  buildChecklist,
  onSaveAndClose,
  onNext,
}) => {
  const progress = buildCart.getProgress();
  const timeUntilText = calculateTimeUntilSurgery(caseData.dateTime);
  const caseDetailsText = `${caseData.caseHospitalId} - ${caseData.caseType} - ${caseData.surgeon}`;
  const locationTimeText = `${caseData.room} - ${caseData.formattedStartTime} - ${timeUntilText}`;
  // Use buildChecklist data if available, otherwise fall back to progress from buildCart
  const traysTotal = buildChecklist?.traysTotal ?? progress.traysRequired;
  const traysAllocated = buildChecklist?.traysAllocated ?? progress.traysLoaded;

  // Popover state for More Options
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);

  const handleMoreOptionsClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleAddCaseNote = () => {
    // TODO: Implement Add Case Note functionality
    console.log('Add Case Note clicked');
    handlePopoverClose();
  };

  const handleResetCase = () => {
    // TODO: Implement Reset Case functionality
    console.log('Reset Case clicked');
    handlePopoverClose();
  };

  const cartCompletenessStatus =
    caseData.cartCompletenessStatus ?? 'Not Started';

  // Calculate progress percentages
  const traysProgress =
    traysTotal > 0 ? (traysAllocated / traysTotal) * 100 : 0;
  // Handle Dispatch button click
  const handleNext = () => {
    // Check if cart is selected before allowing dispatch
    if (buildCart.hasCartSelected()) {
      onNext();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        position: 'sticky',
        zIndex: periverseTheme.zIndex.appBar - 1,
        backgroundColor: '#F7F9FA',
        borderBottom: '1px solid #E0E0E0',
        padding: '1rem 1rem',
      }}
    >
      {/* Top Section: Title */}
      <Box
        sx={{
          paddingTop: '0.3rem',
          paddingBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <UITypography variant="h5" color="textPrimary">
          Case {caseDetailsText}
        </UITypography>
        {cartCompletenessStatus && (
          <StatusBadge>{cartCompletenessStatus}</StatusBadge>
        )}
      </Box>

      {/* Middle Section: Case Details */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
        {/* Calendar Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime
            sx={{
              fontSize: 20,
              color: periverseTheme.palette.text.primary,
            }}
          />
          <UITypography
            variant="body1"
            sx={{ color: periverseTheme.palette.text.primary }}
          >
            {locationTimeText}
          </UITypography>
        </Box>
        {/* Leased Cart Id, for the trays to be loaded */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CardIconWrapper>
            <img src={BuildCartIcon} alt="Build Cart" width={20} height={20} />
          </CardIconWrapper>
          <UITypography
            variant="body1"
            sx={{ color: periverseTheme.palette.text.primary }}
          >
            {buildCart.selectedCartId
              ? `Cart ${buildCart.selectedCartReferenceNumber} - ${buildCart.selectedCartLocation}`
              : '-'}
          </UITypography>
        </Box>

        {/* Trays Progress Row */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'top',
            gap: 1,
          }}
        >
          <Inbox
            sx={{ fontSize: 20, color: periverseTheme.palette.text.primary }}
          />
          <Box
            sx={{
              display: 'flex',

              flexDirection: 'column',
              rowGap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',

                alignItems: 'center',
                gap: 1,
              }}
            >
              <UITypography
                variant="body1"
                sx={{
                  color: periverseTheme.palette.text.primary,
                  whiteSpace: 'nowrap',
                }}
              >
                {traysAllocated}/{traysTotal} Instrument Trays
              </UITypography>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={traysProgress == 0 ? 2.2 : traysProgress}
                  sx={{
                    minWidth: '10.8887rem',

                    height: '0.25rem',
                    borderRadius: 2,
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: periverseColors.peritaspurple[500],
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            </Box>
            {/*//Instrument & Supplies Progress - NOT USING THIS FOR NOW*/}
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UITypography
                variant="body1"
                sx={{
                  color: periverseTheme.palette.text.primary,
                  whiteSpace: 'nowrap',
                }}
              >
                {progress.instrumentsSuppliesLoaded}/
                {progress.instrumentsSuppliesRequired} Instrument & Supplies
              </UITypography>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    instrumentsSuppliesProgress == 0
                      ? 2.5
                      : instrumentsSuppliesProgress
                  }
                  sx={{
                    minWidth: '10.8887rem',
                    height: '0.25rem',
                    borderRadius: '0.25rem',
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: periverseColors.peritaspurple[500],
                      borderRadius: '0.25rem',
                    },
                  }}
                />
              </Box>
            </Box> */}
          </Box>
        </Box>
      </Box>

      {/* Bottom Section: More Options + Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 0',
        }}
      >
        {/* More Options Button with Popover */}
        <Box>
          <Box
            component="button"
            onClick={handleMoreOptionsClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: periverseColors.peritaspurple[500],
              textDecoration: 'none',
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowDropDown
              sx={{
                marginLeft: '-0.4rem',
                fontSize: 20,
                color: periverseColors.peritaspurple[500],
              }}
            />
            <UITypography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: periverseColors.peritaspurple[500],
              }}
            >
              More Options
            </UITypography>
          </Box>
          <Popover
            open={isPopoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  // borderRadius: themeShape.borderRadius.small,
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                  marginTop: '4px',
                  minWidth: '270px',
                },
              },
            }}
          >
            <MenuList
              sx={{
                padding: '8px 0',
              }}
            >
              <MenuItem
                onClick={handleAddCaseNote}
                sx={{
                  padding: '10px 16px',
                  fontSize: '0.875rem',
                  color: periverseTheme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: periverseTheme.palette.action.hover,
                  },
                }}
              >
                Add Case Note
              </MenuItem>
              <MenuItem
                onClick={handleResetCase}
                sx={{
                  padding: '10px 16px',
                  fontSize: '0.875rem',
                  color: periverseTheme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: periverseTheme.palette.action.hover,
                  },
                }}
              >
                Reset Case
              </MenuItem>
            </MenuList>
          </Popover>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <UIButton
            variant="outlined"
            onClick={onSaveAndClose}
            sx={{
              textTransform: 'none',
              borderColor: componentTokens.primary.states.outlinedBorder,
              color: periverseColors.peritaspurple[600],
              '&:hover': {
                borderColor: periverseColors.peritaspurple[600],
                backgroundColor: 'rgba(35, 189, 232, 0.04)',
              },
            }}
          >
            Close & Store for later
          </UIButton>

          <UIButton
            variant="contained"
            onClick={handleNext}
            disabled={!buildCart.hasCartSelected()}
            sx={{
              textTransform: 'none',
              backgroundColor: periverseColors.peritaspurple[600],
              color: periverseTheme.palette.common.white,
              '&:hover': {
                backgroundColor: periverseColors.peritaspurple[700],
              },
              '&:disabled': {
                backgroundColor:
                  periverseTheme.palette.action.disabledBackground,
                color: periverseTheme.palette.action.disabled,
              },
            }}
          >
            Dispatch
          </UIButton>
        </Box>
      </Box>
    </Box>
  );
};
